import { useCallback, useEffect, useState } from 'react'
import { Alert, Box, Button, LinearProgress, List, ListItemButton, ListItemText, Paper, Typography } from '@mui/material'
import { ativosGoogle, ativosMeta, type AtivosGoogle, type AtivosMeta } from '../api/admin'
import { formatarMcc, instrucaoDoDono, PASSO_ADMIN_META, type InfoAtivacao, type PlataformaAtivacao } from '../utils/ativacao'

type Opcao = { id: string; nome: string; detalhe: string }

const META = ['meta_ads', 'instagram', 'facebook']
const GOOGLE = ['google_ads', 'ga4', 'search_console']
export const TEM_BUSCA = new Set([...META, ...GOOGLE])

const PERMISSAO_GSC: Record<string, string> = { siteOwner: 'Proprietário', siteFullUser: 'Total', siteRestrictedUser: 'Restrito' }

/** As opções da plataforma, do que as credenciais do sistema enxergam. */
function opcoesDe(plataforma: string, meta: AtivosMeta | null, google: AtivosGoogle | null): Opcao[] {
  if (meta && plataforma === 'meta_ads') return meta.contasAnuncio.map((c) => ({ id: c.id, nome: c.nome, detalhe: c.id }))
  if (meta && plataforma === 'facebook') return meta.paginas.map((p) => ({ id: p.id, nome: p.nome, detalhe: `Página · ${p.id}` }))
  if (meta && plataforma === 'instagram') {
    return meta.paginas.flatMap((p) =>
      p.instagram ? [{ id: p.instagram.id, nome: p.instagram.usuario ? `@${p.instagram.usuario}` : p.nome, detalhe: `Ligado à página ${p.nome} · ${p.instagram.id}` }] : [],
    )
  }
  if (google && plataforma === 'google_ads') return google.googleAds.map((c) => ({ id: c.id, nome: c.nome, detalhe: `${formatarMcc(c.id)}${c.moeda ? ` · ${c.moeda}` : ''}` }))
  if (google && plataforma === 'ga4') return google.ga4.map((p) => ({ id: p.id, nome: p.nome, detalhe: `${p.conta ? `Conta ${p.conta} · ` : ''}${p.id}` }))
  if (google && plataforma === 'search_console') return google.searchConsole.map((s) => ({ id: s.id, nome: s.id, detalhe: PERMISSAO_GSC[s.permissao] ?? s.permissao }))
  return []
}

/**
 * "Buscar no Meta" / "Buscar no Google": lista o que o acesso do MarketingHUB já enxerga e
 * preenche o ID e o nome com um clique. Lista vazia = o dono da marca ainda não liberou: a tela
 * mostra o passo dele, pronto para copiar.
 */
export function EscolherConta({
  plataforma,
  cadastradas,
  info,
  onEscolher,
}: {
  plataforma: string
  /** IDs já cadastrados nesta plataforma (aparecem, mas não dá para escolher de novo). */
  cadastradas: Set<string>
  info: InfoAtivacao
  onEscolher: (id: string, nome: string) => void
}) {
  const [meta, setMeta] = useState<AtivosMeta | null>(null)
  const [google, setGoogle] = useState<AtivosGoogle | null>(null)
  const [buscando, setBuscando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const ehMeta = META.includes(plataforma)

  const buscar = useCallback(async () => {
    setBuscando(true)
    setErro(null)
    try {
      if (ehMeta) setMeta(await ativosMeta())
      else setGoogle(await ativosGoogle())
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setBuscando(false)
    }
  }, [ehMeta])

  // Já busca ao abrir: é o que a pessoa ia fazer de qualquer jeito.
  useEffect(() => {
    if (TEM_BUSCA.has(plataforma)) buscar()
  }, [plataforma, buscar])

  if (!TEM_BUSCA.has(plataforma)) return null
  const ativos = ehMeta ? meta : google
  const opcoes = opcoesDe(plataforma, meta, google)
  // Só aparecem contas já cadastradas (nesta ou em outra marca): para esta marca, é como lista vazia.
  const livres = opcoes.filter((o) => !cadastradas.has(o.id) && ativos?.cadastradas?.[o.id] === undefined)
  // Só os avisos desta plataforma (no Meta, o das contas de anúncio só vale para o Meta Ads).
  const prefixo: Record<string, string> = { google_ads: 'Google Ads', ga4: 'GA4', search_console: 'Search Console', meta_ads: 'Contas de anúncio' }
  const avisos = (ativos?.avisos ?? []).filter((a) => prefixo[plataforma] !== undefined && a.startsWith(prefixo[plataforma]))
  const infoComPortfolio: InfoAtivacao = { ...info, portfolioMeta: info.portfolioMeta ?? meta?.portfolios?.[0] ?? null }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        {ehMeta ? 'Contas que o acesso do MarketingHUB enxerga no Meta' : 'Contas que o acesso do MarketingHUB enxerga no Google'} — clique para preencher.
      </Typography>
      {buscando ? <LinearProgress sx={{ mb: 1 }} /> : null}
      {erro ? <Alert severity="error">{erro}</Alert> : null}
      {ativos && opcoes.length ? (
        <Paper variant="outlined" sx={{ maxHeight: 240, overflowY: 'auto' }}>
          <List dense disablePadding>
            {opcoes.map((o) => {
              const outra = ativos.cadastradas?.[o.id]
              const ja = cadastradas.has(o.id) || outra !== undefined
              return (
                <ListItemButton key={o.id} disabled={ja} onClick={() => onEscolher(o.id, o.nome)}>
                  <ListItemText primary={o.nome} secondary={ja ? `${o.detalhe} · já cadastrada${outra && !cadastradas.has(o.id) ? ` em ${outra}` : ''}` : o.detalhe} />
                </ListItemButton>
              )
            })}
          </List>
        </Paper>
      ) : null}
      {ativos && !opcoes.length && !buscando && avisos.length ? (
        // Lista vazia por falha da credencial ou da API: não é o dono que precisa agir.
        <Alert severity="warning">
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            O MarketingHUB não conseguiu consultar a plataforma — avise o administrador.
          </Typography>
          {avisos.map((a) => (
            <Typography key={a} variant="body2">
              {a}
            </Typography>
          ))}
        </Alert>
      ) : null}
      {ativos && !livres.length && !buscando && !avisos.length ? (
        <Alert severity="info" sx={{ mt: opcoes.length ? 1 : 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {opcoes.length ? 'As contas que aparecem já estão cadastradas. A desta marca ainda não chegou:' : 'Nada aparece ainda: o dono da marca precisa liberar o acesso.'}
          </Typography>
          <Typography variant="body2">{instrucaoDoDono(plataforma as PlataformaAtivacao, infoComPortfolio)}</Typography>
          {ehMeta ? (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {PASSO_ADMIN_META}
            </Typography>
          ) : null}
        </Alert>
      ) : null}
      {plataforma === 'instagram' && meta?.paginas.some((p) => !p.instagram) ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Páginas sem Instagram profissional ligado: {meta.paginas.filter((p) => !p.instagram).map((p) => p.nome).join(', ')}. Para aparecer aqui, o
          Instagram precisa ser conta profissional e estar vinculado à página (no app do Instagram: Editar perfil → Página).
        </Typography>
      ) : null}
      {(opcoes.length ? avisos : []).map((a) => (
        <Typography key={a} variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
          {a}
        </Typography>
      ))}
      {ativos ? (
        <Button size="small" onClick={buscar} disabled={buscando} sx={{ textTransform: 'none', mt: 0.5 }}>
          {buscando ? 'Consultando…' : 'Buscar de novo'}
        </Button>
      ) : null}
    </Box>
  )
}
